import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Check, Clock, Camera, Mic, Video, Monitor, Palette, 
  Coffee, Users, ChevronRight
} from 'lucide-react';
import ServiceHero from '../../components/services/ServiceHero';
import { Footer } from '../../components/Footer';

const formatIDR = (amount) => {
  return new Intl.NumberFormat('id-ID').format(amount);
};

const StudioRentalPage = () => {

  const hourlyIncluded = [
    '2 x 4K cameras (Sony/Canon)',
    '2 studio microphones',
    '3-point professional lighting',
    'Neutral backdrop (black/white/grey)',
    'Real-time audio monitoring',
    'Raw files delivered after session'
  ];

  const equipmentAddons = [
    { name: 'Extra camera (3rd angle)', price: '+Rp 200,000/hr' },
    { name: '2 extra microphones', price: '+Rp 150,000/hr' },
    { name: 'Teleprompter', price: '+Rp 100,000/hr' },
    { name: 'Green screen', price: '+Rp 150,000/hr' },
    { name: 'RGB custom lighting', price: '+Rp 100,000/hr' }
  ];

  const postProductionAddons = [
    { name: 'Basic edit (cuts only)', price: 'Rp 1,500,000/episode' },
    { name: 'Pro edit (animations, titles, music)', price: 'Rp 2,500,000/episode' },
    { name: '1 Reel (60s)', price: 'Rp 500,000' },
    { name: 'Pack 3 Reels', price: 'Rp 1,200,000' },
    { name: 'Pack 5 Reels', price: 'Rp 1,800,000' },
    { name: 'Transcription', price: 'Rp 400,000/episode' },
    { name: 'Integrated subtitles (FR or EN)', price: 'Rp 600,000/episode' },
    { name: 'YouTube thumbnail', price: 'Rp 300,000/image' },
    { name: 'External audio cleanup (Zoom, phone)', price: 'Rp 300,000/file' }
  ];

  const halfDaySchedule = [
    { time: 'H1', title: 'Setup & Brief', desc: 'Welcome, mic/camera setup, sound & image test, content briefing' },
    { time: 'H2–H3', title: 'Filming', desc: 'Main content recording (1 full episode)' },
    { time: 'H4', title: 'Review & Export', desc: 'Take review with client, raw file export, delivery via Drive or USB' }
  ];

  const fullDaySchedule = [
    { time: 'H1', title: 'Setup & Brief', desc: 'Welcome, full setup, tech test, filming plan for the day' },
    { time: 'H2–H4', title: 'Filming Session 1', desc: 'First content block (2–3 episodes or modules)' },
    { time: 'H4–H4:30', title: 'Lunch Break', desc: 'Partner restaurant at -10%' },
    { time: 'H4:30–H7', title: 'Filming Session 2', desc: 'Second content block' },
    { time: 'H7–H8', title: 'Review & Export', desc: 'Take review, files organized by episode, export & delivery' }
  ];

  const bookingPacks = [
    { id: 'hourly', name: 'Hourly Rental', description: 'Minimum 2 hours', price: 'From Rp 800,000/hr' },
    { id: 'half_day', name: 'Half Day Pack (4h)', description: '4 hours of studio time', price: 4500000 },
    { id: 'full_day', name: 'Full Day Pack (8h)', description: '8 hours of studio time', price: 8000000 }
  ];

  const bookingAddons = [
    { id: 'extra_camera', name: 'Extra camera' },
    { id: 'extra_mics', name: 'Extra mics' },
    { id: 'teleprompter', name: 'Teleprompter' },
    { id: 'green_screen', name: 'Green screen' },
    { id: 'rgb_lighting', name: 'RGB lighting' },
    { id: 'basic_edit', name: 'Basic edit' },
    { id: 'pro_edit', name: 'Pro edit' },
    { id: 'reels', name: 'Reels' },
    { id: 'transcription', name: 'Transcription' },
    { id: 'subtitles', name: 'Subtitles' },
    { id: 'thumbnail', name: 'Thumbnail' }
  ];

  const bookingFields = [
    { id: 'num_people', label: 'Number of people', type: 'number', min: 1, max: 10, placeholder: '2' },
    { id: 'additional_notes', label: 'Additional notes', type: 'textarea', placeholder: 'Any specific requirements...' }
  ];

  return (
    <div className="min-h-screen bg-[#0D0D0D] pt-24">
      <ServiceHero
        tag="STUDIO RENTAL"
        title="Hourly & Half/Full Day"
        description="Professional podcast and video studio with premium equipment and optional dedicated technician."
        bookLink="/services/studio-rental/book"
        heroImage="https://images.unsplash.com/photo-1654023442884-30f6bea363fd?w=1600&q=80"
        metaTitle="Studio Rental | Canggu Podcast Studio"
        metaDescription="Rent our professional podcast and video studio in Canggu, Bali. Hourly, half-day and full-day packages with premium equipment."
      />

      {/* Section 1: Hourly Rates */}
      <section className="py-16 bg-[#0A0A0A]">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="font-heading text-3xl md:text-4xl text-white mb-8">Hourly Rates</h2>
          
          {/* Rate Cards */}
          <div className="grid md:grid-cols-2 gap-6 mb-12">
            <div className="p-6 border border-[#262626] bg-[#121212]">
              <h3 className="text-xl font-semibold text-white mb-2">Studio Only</h3>
              <p className="text-gray-400 text-sm mb-4">No technician included</p>
              <div className="text-3xl font-heading font-bold text-[#F5A623] mb-2">
                Rp 800,000<span className="text-lg text-gray-400">/hour</span>
              </div>
              <p className="text-sm text-gray-500">Minimum 2 hours</p>
            </div>
            
            <div className="p-6 border-2 border-[#F5A623] bg-[#F5A623]/5 relative">
              <span className="absolute -top-3 right-6 px-3 py-1 bg-[#F5A623] text-black text-xs font-semibold">
                RECOMMENDED
              </span>
              <h3 className="text-xl font-semibold text-white mb-2">Studio + Technician ⭐</h3>
              <p className="text-gray-400 text-sm mb-4">Dedicated technician included</p>
              <div className="text-3xl font-heading font-bold text-[#F5A623] mb-2">
                Rp 1,200,000<span className="text-lg text-gray-400">/hour</span>
              </div>
              <p className="text-sm text-gray-500">Minimum 2 hours</p>
            </div>
          </div>

          {/* Included in Base Rate */}
          <div className="mb-12">
            <h3 className="text-xl font-heading font-semibold text-white mb-6">Included in Base Rate</h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {hourlyIncluded.map((item, index) => (
                <div key={index} className="flex items-center gap-3 p-4 bg-[#121212] border border-[#262626]">
                  <Check className="w-5 h-5 text-[#F5A623] flex-shrink-0" />
                  <span className="text-gray-300 text-sm">{item}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Equipment Add-ons */}
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-xl font-heading font-semibold text-white mb-6">Equipment Add-ons (per hour)</h3>
              <div className="space-y-3">
                {equipmentAddons.map((addon, index) => (
                  <div key={index} className="flex items-center justify-between p-4 bg-[#121212] border border-[#262626]">
                    <span className="text-gray-300">{addon.name}</span>
                    <span className="text-[#F5A623] font-semibold">{addon.price}</span>
                  </div>
                ))}
              </div>
            </div>
            
            <div>
              <h3 className="text-xl font-heading font-semibold text-white mb-6">Post-Production Add-ons</h3>
              <div className="space-y-3">
                {postProductionAddons.map((addon, index) => (
                  <div key={index} className="flex items-center justify-between p-4 bg-[#121212] border border-[#262626]">
                    <span className="text-gray-300">{addon.name}</span>
                    <span className="text-[#F5A623] font-semibold">{addon.price}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 2: Half Day Pack */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-6">
          <div className="border border-[#262626] bg-[#121212] p-8 md:p-12">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
              <div>
                <h2 className="font-heading text-3xl md:text-4xl text-white mb-2">Half Day Pack</h2>
                <p className="text-gray-400">4 hours of professional studio time</p>
              </div>
              <div className="mt-4 md:mt-0 text-right">
                <div className="text-4xl font-heading font-bold text-[#F5A623]">
                  Rp 4,500,000
                </div>
              </div>
            </div>

            {/* Schedule */}
            <div className="mb-8">
              <h3 className="text-lg font-semibold text-white mb-4">Schedule</h3>
              <div className="space-y-3">
                {halfDaySchedule.map((item, index) => (
                  <div key={index} className="flex gap-4 p-4 bg-[#0D0D0D] border border-[#262626]">
                    <span className="text-[#F5A623] font-semibold w-20 flex-shrink-0">{item.time}</span>
                    <div>
                      <p className="text-white font-semibold">{item.title}</p>
                      <p className="text-gray-400 text-sm">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Included */}
            <div className="mb-8">
              <h3 className="text-lg font-semibold text-white mb-4">Included</h3>
              <div className="flex flex-wrap gap-3">
                {['2 x 4K cameras', '2 mics', 'Dedicated technician', 'Raw files same day', 'Water & coffee'].map((item, i) => (
                  <span key={i} className="px-4 py-2 bg-[#F5A623]/10 text-[#F5A623] text-sm border border-[#F5A623]/30">
                    {item}
                  </span>
                ))}
              </div>
            </div>

            {/* Upsells */}
            <div>
              <h3 className="text-lg font-semibold text-white mb-4">Upsells</h3>
              <div className="grid md:grid-cols-2 gap-3">
                {[
                  { name: 'Basic edit', price: '+Rp 1,500,000' },
                  { name: '1 Reel', price: '+Rp 500,000' },
                  { name: 'Full equipment setup (3 cam + 4 mics)', price: '+Rp 700,000' },
                  { name: 'Transcription', price: '+Rp 400,000' },
                  { name: 'Headshot session (30 min)', price: '+Rp 1,500,000' }
                ].map((item, i) => (
                  <div key={i} className="flex items-center justify-between p-3 bg-[#0D0D0D] border border-[#262626]">
                    <span className="text-gray-300">{item.name}</span>
                    <span className="text-[#F5A623] font-semibold">{item.price}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 3: Full Day Pack */}
      <section className="py-16 bg-[#0A0A0A]">
        <div className="max-w-6xl mx-auto px-6">
          <div className="border-2 border-[#F5A623] bg-[#F5A623]/5 p-8 md:p-12 relative">
            <span className="absolute -top-3 left-8 px-4 py-1 bg-[#F5A623] text-black text-sm font-semibold">
              BEST VALUE
            </span>
            
            <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
              <div>
                <h2 className="font-heading text-3xl md:text-4xl text-white mb-2">Full Day Pack</h2>
                <p className="text-gray-400">8 hours of professional studio time</p>
              </div>
              <div className="mt-4 md:mt-0 text-right">
                <div className="text-4xl font-heading font-bold text-[#F5A623]">
                  Rp 8,000,000
                </div>
              </div>
            </div>

            {/* Schedule */}
            <div className="mb-8">
              <h3 className="text-lg font-semibold text-white mb-4">Schedule</h3>
              <div className="space-y-3">
                {fullDaySchedule.map((item, index) => (
                  <div key={index} className="flex gap-4 p-4 bg-[#0D0D0D] border border-[#262626]">
                    <span className="text-[#F5A623] font-semibold w-24 flex-shrink-0">{item.time}</span>
                    <div>
                      <p className="text-white font-semibold">{item.title}</p>
                      <p className="text-gray-400 text-sm">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Included */}
            <div className="mb-8">
              <h3 className="text-lg font-semibold text-white mb-4">Included</h3>
              <div className="flex flex-wrap gap-3">
                {[
                  '2 x 4K cameras', '2 mics', 'Dedicated technician all day', 
                  'Raw files organized by episode', 'Coffee/water/snacks', 'Partner restaurant -10%'
                ].map((item, i) => (
                  <span key={i} className="px-4 py-2 bg-[#F5A623]/10 text-[#F5A623] text-sm border border-[#F5A623]/30">
                    {item}
                  </span>
                ))}
              </div>
            </div>

            {/* Upsells */}
            <div>
              <h3 className="text-lg font-semibold text-white mb-4">Upsells</h3>
              <div className="grid md:grid-cols-2 gap-3">
                {[
                  { name: 'Full equipment setup (3 cam + 4 mics)', price: '+Rp 1,200,000/day' },
                  { name: 'Edit 1 episode (basic)', price: '+Rp 1,500,000' },
                  { name: 'Edit full day (all episodes)', price: '+Rp 5,000,000' },
                  { name: 'Pack 3 Reels', price: '+Rp 1,200,000' },
                  { name: 'Transcriptions', price: '+Rp 400,000/episode' },
                  { name: 'Photo session 1h (personal brand)', price: '+Rp 3,500,000' }
                ].map((item, i) => (
                  <div key={i} className="flex items-center justify-between p-3 bg-[#0D0D0D] border border-[#262626]">
                    <span className="text-gray-300">{item.name}</span>
                    <span className="text-[#F5A623] font-semibold">{item.price}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-heading text-3xl md:text-4xl text-white mb-4">
            Ready to Book Your Session?
          </h2>
          <p className="text-gray-400 mb-8">
            Choose your package and let's create amazing content together.
          </p>
          <Link
            to="/services/studio-rental/book"
            className="px-12 py-4 bg-[#F5A623] text-black font-semibold text-lg hover:bg-[#FFB84D] transition-colors inline-block"
            data-testid="book-now-cta"
          >
            Book Now
          </Link>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default StudioRentalPage;
